import { createContext, useContext, useState, useCallback, useRef, useEffect, type ReactNode } from 'react'
import { buildDebugConfig, isLocalhost, persistDebugState, clearDebugState, type DebugConfig } from '../config/debug'

/** Shape of the debug context exposed to consumers for inspecting and toggling debug flags at runtime. */
interface DebugContextValue {
  config: DebugConfig                              /** The current debug configuration object. */
  unlocked: boolean                                /** Whether the debug panel is unlocked via the 4-click sequence. */
  registerUnlockClick: () => void                  /** Register a click towards the 4-click unlock sequence. */
  lock: () => void                                 /** Lock the debug panel and clear unlock state. */
  setOverride: (path: string, value: boolean | number | string | string[] | null) => void  /** Set a runtime override at a dot-separated path. */
  commitOverrides: () => void                      /** Persist the current config to localStorage. */
  resetAll: () => void                             /** Clear all persisted debug data and rebuild from defaults. */
  productionMode: boolean                          /** Whether debug overrides are persisted. */
  setProductionMode: (v: boolean) => void          /** Toggle production mode (persisted config). */
}

/** Context with null default; consumers must use useDebug() which throws if called outside DebugProvider. */
const DebugCtx = createContext<DebugContextValue | null>(null)

/** Hook to access the debug context. Throws with a descriptive error if used outside DebugProvider. */
// eslint-disable-next-line react-refresh/only-export-components
export const useDebug = (): DebugContextValue => {
  const ctx = useContext(DebugCtx)
  if (!ctx) {
    throw new Error('useDebug must be used within a DebugProvider')
  }
  return ctx
}

/** Performs a shallow or nested update on the DebugConfig object using a dot-separated path (e.g. "section.key"). Supports 1-level nesting. */
function updateConfigAtPath(prev: DebugConfig, path: string, value: boolean | number | string | string[] | null): DebugConfig {
  const parts = path.split('.')
  if (parts.length === 1) {
    return { ...prev, [parts[0]]: value }
  }
  if (parts.length === 2) {
    const [parent, child] = parts
    const parentObj = prev[parent as keyof DebugConfig]
    if (typeof parentObj === 'object' && parentObj !== null) {
      return { ...prev, [parent]: { ...parentObj, [child]: value } }
    }
  }
  return prev
}

/** Provides debug configuration state, unlock mechanism, and persistence to the component tree. */
export const DebugProvider = ({ children }: { children: ReactNode }) => {
  // Initialize debug config from defaults (buildDebugConfig reads localStorage if production mode was active).
  // WHY LOCALHOST-ONLY: `unlocked` only available on localhost (isLocalhost() check).
  // Falls back to false if localStorage unavailable. Production mode defaults to false
  // but can be persisted.
  const [config, setConfig] = useState<DebugConfig>(buildDebugConfig)
  // Unlock state: only accessible on localhost, persisted across sessions via localStorage.
  const [unlocked, setUnlocked] = useState(() => {
    if (!isLocalhost()) return false
    try {
      return localStorage.getItem('ma_debug_unlocked') === '1'
    } catch {
      return false
    }
  })
  // Production mode: when enabled, debug overrides get persisted to localStorage for testing on deployed builds.
  const [productionMode, setProductionModeState] = useState(() => {
    try {
      return localStorage.getItem('ma_debug_prod_mode') === '1'
    } catch {
      return false
    }
  })

  /**
   * Ref that always holds the latest config so callbacks (e.g. commitOverrides,
   * setProductionMode) can read the current value without stale closures.
   * WHY useRef + configRef.current = config: Prevents stale closures in callbacks.
   * The useCallback captures the initial config, but configRef.current always has the latest.
   */
  const configRef = useRef(config)

  // Keep ref in sync with latest config so callbacks read current values
  useEffect(() => {
    configRef.current = config
  }, [config])

  /** Tracks consecutive clicks for the 4-click unlock sequence. */
  const clickCountRef = useRef(0)
  /** Timer that resets the click count after 2 seconds of inactivity. */
  const resetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  /**
   * Guards against persisting on the initial render (only persist subsequent config changes).
   * WHY IS FIRST RENDER: Prevents persisting the default config to localStorage on the
   * very first render. Without this, every page load on localhost would overwrite custom
   * overrides with defaults. Only persists config changes AFTER the first render.
   */
  const isFirstRender = useRef(true)

  /** Persists debug config to localStorage whenever it changes, but only when productionMode is active (skip initial render). */
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }
    if (productionMode) {
      persistDebugState(config)
    }
  }, [config, productionMode])

  /**
   * Registers a click toward the 4-click unlock sequence. Resets the counter if
   * 2 seconds pass between clicks. Only works on localhost.
   * WHY 4 CLICKS (not 5): Different threshold from DeveloperMode's 5-click. Debug
   * panel unlock is intentionally easier (4 clicks vs 5) because it is a development tool.
   */
  const registerUnlockClick = useCallback(() => {
    if (!isLocalhost()) return

    if (resetTimerRef.current) clearTimeout(resetTimerRef.current)
    resetTimerRef.current = setTimeout(() => {
      clickCountRef.current = 0
    }, 2000)

    clickCountRef.current += 1
    if (clickCountRef.current >= 4) {
      setUnlocked(true)
      try {
        localStorage.setItem('ma_debug_unlocked', '1')
      } catch {
        // silent
      }
      clickCountRef.current = 0
    }
  }, [])

  /** Locks the debug panel and removes the unlock flag from localStorage. */
  const lock = useCallback(() => {
    setUnlocked(false)
    try {
      localStorage.removeItem('ma_debug_unlocked')
    } catch {
      // silent
    }
  }, [])

  /** Sets a runtime override at the given dot-separated path (e.g. "section.key") without persisting. */
  const setOverride = useCallback((path: string, value: boolean | number | string | string[] | null) => {
    setConfig((prev) => updateConfigAtPath(prev, path, value))
  }, [])

  /**
   * Persists the current config (read from the ref to avoid stale closure) to localStorage via persistDebugState.
   * WHY configRef.current: Explicitly reads from configRef.current (not the closure `config`)
   * to avoid stale closure issues. The useCallback captures the initial config, but
   * configRef.current always has the latest.
   */
  const commitOverrides = useCallback(() => {
    persistDebugState(configRef.current)
  }, [])

  /** Clears all persisted debug state and rebuilds the config from scratch using buildDebugConfig defaults. */
  const resetAll = useCallback(() => {
    clearDebugState()
    setConfig(buildDebugConfig())
  }, [])

  /**
   * Enables or disables production mode. When enabled, persists config; when disabled, removes the flag.
   * WHY IMMEDIATE PERSIST: When enabling production mode, immediately persists the current config.
   * This ensures the config survives page reload.
   */
  const setProductionMode = useCallback((v: boolean) => {
    setProductionModeState(v)
    if (v) {
      persistDebugState(configRef.current)
      try {
        localStorage.setItem('ma_debug_prod_mode', '1')
      } catch {
        // silent
      }
    } else {
      try {
        localStorage.removeItem('ma_debug_prod_mode')
      } catch {
        // silent
      }
    }
  }, [])

  return (
    <DebugCtx.Provider
      value={{
        config,
        unlocked,
        registerUnlockClick,
        lock,
        setOverride,
        commitOverrides,
        resetAll,
        productionMode,
        setProductionMode,
      }}
    >
      {children}
    </DebugCtx.Provider>
  )
}
